import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Col,
  Input,
  Layout,
  Row,
  Select,
  Modal,
  Typography,
  message,
} from 'antd';
import { get, omit } from 'lodash';

import LocationServices from 'services/location';

import PdButton from 'components/pd-button';
import { TableList } from 'components/table-list';

import { ProfileContext } from 'contexts/profile';

import {
  ACTIVE_INACTIVE_STATUS_FILTER_LIST,
  errHandling,
  formatStatus,
} from 'utils';
import queryString from 'query-string';

import './style.scss';

const { Content } = Layout;
const { Option } = Select;
const { Search } = Input;
const { Text } = Typography;
const { confirm } = Modal;

export function Location() {
  const location = useLocation();
  const { fetchProfile } = useContext(ProfileContext);

  const [currentPage, setCurrentPage] = useState(0);
  const [loadStatus, setLoadStatus] = useState(true);
  const [nextPage, setNextPage] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [status, setStatus] = useState('');
  const [locations, setLocations] = useState([]);
  const [id, setId] = useState(-1);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');

  const navigate = useNavigate();
  const parsed = queryString.parse(location.search);

  function handleDeactivateLocation(locationId) {
    confirm({
      title: 'Are you sure deactivate this location?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      async onOk() {
        setLoadStatus(true);
        try {
          await LocationServices.deactivateLocation(locationId);
          fetchLocation();
        } catch (error) {
          errHandling(error);
        } finally {
          setLoadStatus(false);
        }
      },
    });
  }

  async function handleActivateLocation(locationId) {
    setLoadStatus(true);
    try {
      await LocationServices.activateLocation(locationId);
      fetchLocation();
      message.success('Successfully restore the location');
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadStatus(false);
    }
  }

  function handleDeleteLocation(locationId) {
    confirm({
      title: 'Are you sure remove this location?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      async onOk() {
        setLoadStatus(true);
        try {
          await LocationServices.deleteLocation(locationId);
          fetchLocation();
        } catch (error) {
          errHandling(error);
        } finally {
          setLoadStatus(false);
        }
      },
    });
  }

  const buttonAction = (row = {}) => {
    const { id = '', status = '' } = row;
    return (
      <div className="button-table">
        <Row>
          {row.warehouses.findIndex((item) => item.primary) === -1 ? (
            <Col span={12} className="button">
              {status === 'active' ? (
                <PdButton
                  className="height-32"
                  type="danger"
                  outline
                  full
                  onClick={handleDeactivateLocation.bind(this, id)}>
                  Deactivate
                </PdButton>
              ) : (
                <PdButton
                  className="height-32"
                  type="primary"
                  outline
                  full
                  onClick={handleActivateLocation.bind(this, id)}>
                  Reactivate
                </PdButton>
              )}
            </Col>
          ) : (
            <Col span={12} />
          )}

          <Col span={12} className="button">
            {status === 'active' ? (
              <PdButton
                className="height-32"
                type="primary"
                full
                onClick={() => {
                  setOpen(true);
                  setId(id);
                  setTitle(
                    locations.find((location) => location.id === id).title
                  );
                }}>
                Edit
              </PdButton>
            ) : (
              <PdButton
                className="height-32"
                type="danger"
                full
                onClick={handleDeleteLocation.bind(this, id)}>
                Delete
              </PdButton>
            )}
          </Col>
        </Row>
      </div>
    );
  };

  const locationStatus = (row) => (
    <div className="status-indicator">
      <span className={row.status !== 'inactive' ? 'active' : 'hidden'}>
        {displayStatus(row.status)}
      </span>
    </div>
  );

  const columns = [
    {
      name: 'ID',
      selector: row => row.id,
      width: '25%',
      center: true,
    },
    {
      name: 'Location',
      selector: row => row.title,
      width: '25%',
      center: true,
    },
    {
      name: 'Status',
      selector: row => row.status,
      width: '25%',
      cell: locationStatus,
      center: true,
    },
    {
      name: '',
      cell: buttonAction,
      width: '25%',
    },
  ];

  function displayStatus(status) {
    if (status === '') {
      return '';
    } else {
      return status.charAt(0).toUpperCase() + status.slice(1);
    }
  }

  async function fetchLocation({ page, status, q } = {}) {
    setLoadStatus(true);
    try {
      const { data } = await LocationServices.getLocations({
        cursor: page,
        status,
        q,
      });
      const res = get(data, 'data', []);
      const cursor = get(data, 'metadata.next_cursor', -1);
      setLocations(res);
      setNextPage(cursor);
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadStatus(false);
    }
  }

  function changePage(page) {
    if (page < 1) page = 1;
    parsed['page'] = page;
    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/locations',
      search: stringified,
    });
  }

  function resetFilter() {
    const stringified = queryString.stringify(
      omit(parsed, ['limit', 'page', 'status', 'q'])
    );
    navigate({
      pathname: '/locations',
      search: stringified,
    });
  }

  function filterLocation(param, val) {
    if (val === '' || val === 0) {
      delete parsed[param];
    } else {
      parsed[param] = val;
    }
    parsed['page'] = 1;
    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/locations',
      search: stringified,
    });
  }

  async function addWarehouse() {
    try {
      const payload = {
        location: {
          title,
        },
      };
      if (id !== -1) {
        await LocationServices.updateLocation(id, payload);
      } else {
        await LocationServices.createLocation(payload);
      }
      setOpen(false);
      setId(-1);
      setTitle('');
      fetchLocation();
    } catch (error) {
      errHandling(error);
    }
  }

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* eslint-disable */
  useEffect(() => {
    if (parsed['page']) {
      if (parsed['page'] < 1) {
        parsed['page'] = 1;
        const stringified = queryString.stringify(parsed);
        navigate({
          pathname: '/locations',
          search: stringified,
        });
      } else setCurrentPage(parsed['page']);
    } else setCurrentPage(1);

    if (parsed['q']) setSearchValue(parsed['q']);
    else setSearchValue('');

    if (parsed['status']) setStatus(parsed['status']);
    else setStatus('');

    fetchLocation({
      page: parsed['page'],
      q: parsed['q'],
      status: parsed['status'],
    });
  }, [parsed['page'], parsed['status'], parsed['q']]);
  /* eslint-enable */

  return (
    <Layout className="main-container pd-cms-location">
      <div className="content">
        <div className="pageTitle">Location</div>
        <Content className="pageContent">
          <Row gutter={[18, 36]}>
            <Col className="mt-1" lg={7}>
              <Search
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                enterButton
                className="search-product"
                placeholder="Search Location"
                onSearch={filterLocation.bind(this, 'q')}
              />
            </Col>
            <Col lg={3} className="mt-1">
              <Select
                className="w-full"
                onChange={filterLocation.bind(this, 'status')}
                value={status}>
                {ACTIVE_INACTIVE_STATUS_FILTER_LIST.map((status) =>
                  !status ? (
                    <Option value={status} selected hidden key={status}>
                      Status
                    </Option>
                  ) : (
                    <Option value={status} key={status}>
                      {formatStatus(status)}
                    </Option>
                  )
                )}
              </Select>
            </Col>
            <Col className="mt-1" lg={4}>
              <span className="reset-filter" onClick={resetFilter}>
                Reset Filter
              </span>
            </Col>
            <Col lg={{ span: 4, offset: 5 }}>
              <PdButton
                className="height-32"
                type="primary"
                full
                outline
                onClick={() => setOpen(true)}>
                Add New Location
              </PdButton>
            </Col>
          </Row>

          <Row>
            {!loadStatus && (
              <TableList
                changePage={changePage}
                columns={columns}
                loadingOrder={loadStatus}
                nextPage={nextPage}
                orderList={locations}
                page={currentPage}
                responsive={false}
              />
            )}
          </Row>
        </Content>
      </div>
      {open && (
        <Modal
          title={`${id !== -1 ? 'Update' : 'Add'} Location`}
          open={open}
          centered
          onCancel={() => {
            setOpen(false);
            setTitle('');
          }}
          footer={false}>
          <Row>
            <div
              className="w-full"
              style={{
                display: 'flex',
                gap: '24px',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
              <Text>Location Name</Text>
              <Input
                placeholder="Input location"
                style={{
                  width: '300px',
                }}
                onChange={(e) => setTitle(e.target.value)}
                value={title}
              />
            </div>
            <div
              className="w-full"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '24px',
                marginTop: '24px',
              }}>
              <PdButton
                className="height-32 w-full"
                type="primary"
                full
                outline
                onClick={() => {
                  setOpen(false);
                  setTitle('');
                }}>
                Cancel
              </PdButton>
              <PdButton
                className="height-32 w-full"
                type="primary"
                full
                onClick={addWarehouse}>
                {id !== -1 ? 'Update' : 'Create'}
              </PdButton>
            </div>
          </Row>
        </Modal>
      )}
    </Layout>
  );
}

export default { Location };
